import * as React from "react";
import { MultiSelect } from "@/lib/shadcn/components/multi-select";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { TagMultiSelect } from "@/lib/shadcn/components/tag-multi-select";
import { Button } from "@/lib/shadcn/ui/button";
import { Switch } from "@/lib/shadcn/ui/switch";
import { Label } from "@/lib/shadcn/ui/label";
import { useAppData } from "@/hooks/useAppData";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { useAuth } from "@/hooks/useAuth";
import { useActiveApartments, useAllApartments } from "@/store/slices/apartments";

/**
 * Traceability: FR/EC IDs reference
 * .verified/features/apartment-filters-everywhere/spec.md (shared filter infra
 * rendered on every in-scope page across all tiers).
 *
 * Shared apartment-filter bar: six controls (Number of Bedrooms, Apartments,
 * Municipality Section, Owner Group, Group, Tags) wired to the global
 * `apartmentFilters` slice via `useApartmentFilters`; options from `useAppData`
 * and the apartments slice.
 *
 * "Clear all filters" is always mounted and toggled via `disabled` (FR-005):
 * conditionally mounting it would yank keyboard focus mid-interaction (a11y
 * bug), whereas a disabled control keeps the focus ring stable.
 *
 * Layout: `flex-col` stack ≤ 1024px → `lg:flex-row` above, to avoid overflow on
 * tablets/small laptops (EC-007). Above 1024px the row also wraps, and the bar
 * carries `min-w-0`: it is a flex *item* in each page's header, and a flex item
 * defaults to `min-width: auto`, i.e. it refuses to shrink below its content. Drop
 * `min-w-0` and the bar grows to fit all its controls, pushes the header past the
 * viewport (horizontal scrollbar on a laptop), and `flex-wrap` never fires. With
 * it, the controls wrap onto a second row instead.
 *
 * The Apartments control uses a count as its trigger text rather than the joined
 * apartment names — names are long enough ("Academy Elegant Apartment #202") that
 * two selections alone would blow out the bar's width.
 *
 * Accessibility: the bar is one `role="group"` named "Apartment filters"; each
 * control sits in its own labelled `role="group"` (the shadcn primitives expose
 * no `aria-label`); a visually-hidden polite live region announces "Filters
 * applied" on slice changes so AT users get an audible cue.
 */
export const ApartmentFilterBar: React.FC = () => {
    const {
        numberOfBedroomsOptions,
        activeMunicipalitySectionOptions,
        municipalitySections,
        activeOwnerGroupOptions,
        groupOptions,
    } = useAppData();
    const {
        filters,
        setBedrooms,
        setMunicipalitySection,
        setOwnerGroup,
        setGroup,
        setTagIds,
        setApartmentIds,
        setShowDecommissioned,
        clearAll,
    } = useApartmentFilters();
    const { user } = useAuth();

    // Offer the same apartments the rest of the bar can actually surface: with
    // "Show decommissioned" off, a decommissioned apartment is filtered out by
    // the active-as-of-today predicate anyway, so listing it would only let the
    // user pick an option that yields nothing.
    const allApartments = useAllApartments();
    const activeApartments = useActiveApartments();
    const selectableApartments = filters.showDecommissioned ? allApartments : activeApartments;
    const apartmentOptions = React.useMemo(
        () =>
            [...selectableApartments]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((apartment) => ({ value: apartment.id.toString(), label: apartment.name })),
        [selectableApartments],
    );
    // Owner Group, Tags, and "Show decommissioned" are admin-only
    // portfolio-management concerns — gate them so user pages (Trends, Trend
    // Graphs) get a trimmed bar while admin pages keep the full set.
    const isAdmin = user?.isAdmin ?? false;

    // address-normalization/EC-014: a municipality-section id persisted before
    // that section was removed no longer resolves to anything, and the raw
    // `a.municipalitySectionId === id` predicate would then silently empty every
    // grid with no explanation. Resolve the persisted id against the KNOWN
    // sections and degrade to "no filter" when it names none of them.
    //
    // Deliberately checked against all known sections, NOT against the
    // apartment-derived `activeMunicipalitySectionOptions`: a section that
    // exists but currently holds no apartments is a legitimate selection whose
    // empty result is the honest answer (apartment-filters-everywhere/EC-003).
    // Only an id naming no section at all is stale. Guarded on the sections
    // fetch having landed, so an in-flight load never clears a valid selection.
    React.useEffect(() => {
        const selected = filters.municipalitySectionId;
        if (selected === 0 || municipalitySections.length === 0) {
            return;
        }
        if (!municipalitySections.some((section) => section.id === selected)) {
            setMunicipalitySection(0);
        }
        // `setMunicipalitySection` is a fresh dispatch closure each render; the
        // guards above make re-running harmless and idempotent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.municipalitySectionId, municipalitySections]);

    const hasActiveFilters =
        filters.bedrooms !== 0 ||
        filters.municipalitySectionId !== 0 ||
        filters.ownerGroupId !== 0 ||
        filters.groupId !== 0 ||
        filters.tagIds.length > 0 ||
        filters.apartmentIds.length > 0;

    // Live-region announcement on slice change. Deliberately generic ("Filters
    // applied") rather than per-dimension. The empty-string initial render
    // avoids announcing on first mount.
    const [announcement, setAnnouncement] = React.useState("");
    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setAnnouncement("Filters applied");
    }, [
        filters.bedrooms,
        filters.municipalitySectionId,
        filters.ownerGroupId,
        filters.groupId,
        filters.tagIds,
        filters.apartmentIds,
    ]);

    return (
        <div
            className="flex min-w-0 flex-col lg:flex-row lg:flex-wrap gap-2"
            data-testid="apartment-filter-bar"
            role="group"
            aria-label="Apartment filters"
        >
            <div data-testid="apartment-filter-bedrooms" role="group" aria-label="Number of Bedrooms filter">
                <SearchableSelect
                    variant="volt"
                    onChange={(value) => setBedrooms(Number(value))}
                    placeholder="Select Number of Bedrooms"
                    options={numberOfBedroomsOptions || []}
                    selectedValue={filters.bedrooms.toString()}
                    selectedValuePrefix="Number of Bedrooms"
                    width="w-auto"
                />
            </div>
            <div data-testid="apartment-filter-apartments" role="group" aria-label="Apartments filter">
                <MultiSelect
                    variant="volt"
                    options={apartmentOptions}
                    selectedValues={filters.apartmentIds.map((id) => id.toString())}
                    onChange={(values) => setApartmentIds(values.map(Number))}
                    placeholder="Select apartments..."
                    searchLabel="Search apartments"
                    // Apartment names are long ("Academy Elegant Apartment #202").
                    // The default trigger text joins the selected labels, which
                    // sizes the button to that string and pushes the filter bar
                    // past the viewport. A count keeps the trigger a fixed size;
                    // the full list stays available via the trigger's tooltip.
                    staticText={
                        filters.apartmentIds.length > 0 ? `Apartments: ${filters.apartmentIds.length} selected` : undefined
                    }
                    className="w-[200px]"
                />
            </div>
            <div data-testid="apartment-filter-municipality" role="group" aria-label="Municipality Section filter">
                <SearchableSelect
                    variant="volt"
                    onChange={(value) => setMunicipalitySection(Number(value))}
                    placeholder="Select Municipality Section"
                    options={activeMunicipalitySectionOptions || []}
                    selectedValue={filters.municipalitySectionId.toString()}
                    selectedValuePrefix="Municipality Section"
                    width="w-auto"
                />
            </div>
            {isAdmin && (
                <div data-testid="apartment-filter-owner-group" role="group" aria-label="Owner Group filter">
                    <SearchableSelect
                        variant="volt"
                        // Owner Group and Group sit side by side in this bar and
                        // are different entities. Without an explicit
                        // `aria-label` both triggers compute the same accessible
                        // name from their placeholder until a value is picked,
                        // so a screen reader announces two identical comboboxes
                        // (WCAG 4.1.2).
                        aria-label="Owner Group"
                        onChange={(value) => setOwnerGroup(Number(value))}
                        placeholder="Select Owner Group"
                        options={activeOwnerGroupOptions || []}
                        selectedValue={filters.ownerGroupId.toString()}
                        selectedValuePrefix="Owner Group"
                        width="w-auto"
                    />
                </div>
            )}
            {isAdmin && (
                <div data-testid="apartment-filter-group" role="group" aria-label="Group filter">
                    <SearchableSelect
                        variant="volt"
                        aria-label="Group"
                        onChange={(value) => setGroup(Number(value))}
                        placeholder="Select Group"
                        options={groupOptions || []}
                        selectedValue={filters.groupId.toString()}
                        selectedValuePrefix="Group"
                        width="w-auto"
                    />
                </div>
            )}
            {isAdmin && (
                <div data-testid="apartment-filter-tags" role="group" aria-label="Tags filter">
                    <TagMultiSelect
                        variant="volt"
                        selectedTagIds={filters.tagIds}
                        onChange={setTagIds}
                        placeholder="Select tags..."
                        className="w-auto"
                    />
                </div>
            )}
            {isAdmin && (
                <div
                    className="flex items-center gap-2"
                    data-testid="apartment-filter-show-decommissioned"
                    role="group"
                    aria-label="Show decommissioned apartments filter"
                >
                    <Switch
                        id="show-decommissioned-toggle"
                        data-testid="show-decommissioned-toggle"
                        checked={filters.showDecommissioned}
                        onCheckedChange={setShowDecommissioned}
                    />
                    <Label htmlFor="show-decommissioned-toggle">Show decommissioned</Label>
                </div>
            )}
            <Button
                variant="outline"
                onClick={clearAll}
                disabled={!hasActiveFilters}
                aria-disabled={!hasActiveFilters}
                data-testid="apartment-filter-clear-all"
            >
                Clear all filters
            </Button>
            <div role="status" aria-live="polite" className="sr-only">
                {announcement}
            </div>
        </div>
    );
};

export default ApartmentFilterBar;
